import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, ChevronRight, ChevronDown } from 'lucide-react';
import { campaignsApi } from '../../lib/api';
import { CampaignStatusChip } from './CampaignStatusChip';
import type { CampaignRecipient, CampaignRecipientStatus, CampaignRun } from '../../types/campaign';
import type { PaginationMeta } from '../../types';

interface Props {
  campaignId: string;
  run: CampaignRun;
  onClose: () => void;
}

// DISPATCHED is a valid CampaignRecipientStatus but the pipeline never
// assigns it to a recipient row (it only ever writes PENDING, SUPPRESSED,
// INVALID, ACCEPTED, or FAILED), so it's left out of this filter — picking
// it would always show zero rows.
const RECIPIENT_STATUSES: CampaignRecipientStatus[] = ['PENDING', 'SUPPRESSED', 'INVALID', 'ACCEPTED', 'FAILED'];

type CountKey = 'resolvedCount' | 'dedupedCount' | 'suppressedCount' | 'invalidCount' | 'dispatchedCount' | 'acceptedCount' | 'failedCount';

const COUNT_LADDER: Array<{ key: CountKey; label: string }> = [
  { key: 'resolvedCount', label: 'Resolved' },
  { key: 'dedupedCount', label: 'Deduped' },
  { key: 'suppressedCount', label: 'Suppressed' },
  { key: 'invalidCount', label: 'Invalid' },
  { key: 'dispatchedCount', label: 'Dispatched' },
  { key: 'acceptedCount', label: 'Accepted' },
  { key: 'failedCount', label: 'Failed' },
];

const PAGE_SIZE = 25;

/** A recipient's mergeValues, sorted by key, for the expand panel's key/value table. */
function sortedMergeEntries(mergeValues: Record<string, string | null> | null): Array<[string, string | null]> {
  if (!mergeValues) return [];
  return Object.entries(mergeValues).sort(([a], [b]) => a.localeCompare(b));
}

/** Reads the server's error message off an Axios rejection, falling back to a generic message. */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === 'string' && msg) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Drill-in view for a single campaign run: the resolution-to-delivery count
 * ladder, plus the per-recipient outcome table behind it. Models
 * SyncRunDetailModal's overlay/panel shell. A FAILED or PARTIAL run can be
 * requeued in place from here — the recovery path for a wedged send.
 */
export function RunDetailModal({ campaignId, run, onClose }: Props) {
  const [currentRun, setCurrentRun] = useState<CampaignRun>(run);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<CampaignRecipientStatus | 'ALL'>('ALL');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isResuming, setIsResuming] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Debounce the free-text email search before it drives a recipients fetch.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim()), 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  // Any filter change restarts pagination at page 1 and collapses expanded rows —
  // the recipient set underneath them is about to change.
  useEffect(() => {
    setPage(1);
    setExpandedIds(new Set());
  }, [statusFilter, search]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const result = await campaignsApi.runDetail(campaignId, run.id, {
          status: statusFilter === 'ALL' ? undefined : statusFilter,
          search: search || undefined,
          page,
          limit: PAGE_SIZE,
        });
        if (cancelled) return;
        setCurrentRun(result.run);
        setRecipients(result.recipients);
        setPagination(result.pagination);
      } catch {
        if (!cancelled) {
          setRecipients([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId, run.id, statusFilter, search, page]);

  async function handleResume() {
    setIsResuming(true);
    try {
      const updated = await campaignsApi.resume(campaignId, run.id);
      setCurrentRun(updated);
      toast.success('Run requeued');
      setStatusFilter('ALL');
      setSearchDraft('');
      setSearch('');
      setPage(1);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to resume run'));
    } finally {
      setIsResuming(false);
    }
  }

  // /resume always 409s a run refused for exceeding the recipient ceiling —
  // that refusal moves the campaign off SENDING, and resume only accepts a
  // FAILED/PARTIAL run on a campaign still SENDING. The campaign page's
  // "Send anyway" flow (bound to the exact refused run/audience) is the
  // real recovery path for that case.
  const isOverLimit = currentRun.failureCode === 'AUDIENCE_OVER_LIMIT';
  const canResume = (currentRun.status === 'FAILED' || currentRun.status === 'PARTIAL') && !isOverLimit;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-mono text-sm flex items-center gap-2">
            <span>{currentRun.kind} run —</span>
            <CampaignStatusChip status={currentRun.status} />
          </h2>
          <button onClick={onClose} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-gray-600 dark:text-gray-400">Started</dt>
            <dd>{currentRun.startedAt ?? '—'}</dd>
            <dt className="text-gray-600 dark:text-gray-400">Finished</dt>
            <dd>{currentRun.finishedAt ?? '—'}</dd>
            <dt className="text-gray-600 dark:text-gray-400">Trigger</dt>
            <dd>{currentRun.triggerType ?? '—'}</dd>
            {currentRun.triggeredByUser && (
              <>
                <dt className="text-gray-600 dark:text-gray-400">Triggered by</dt>
                <dd>{currentRun.triggeredByUser.displayName ?? currentRun.triggeredByUser.email}</dd>
              </>
            )}
            {currentRun.errorMessage && (
              <>
                <dt className="text-gray-600 dark:text-gray-400">Error</dt>
                <dd className="text-red-600 dark:text-red-400">{currentRun.errorMessage}</dd>
              </>
            )}
          </dl>

          <div className="flex flex-wrap gap-4 text-sm border-y border-gray-200 dark:border-gray-700 py-3">
            {COUNT_LADDER.map(({ key, label }) => (
              <div key={key}>
                <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{currentRun[key] ?? '—'}</p>
              </div>
            ))}
          </div>

          {canResume && (
            <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This run {currentRun.status === 'FAILED' ? 'failed' : 'finished partially'}. Resuming requeues its remaining recipients.
              </p>
              <button type="button" className="btn-primary whitespace-nowrap" onClick={handleResume} disabled={isResuming}>
                {isResuming ? 'Resuming…' : 'Resume run'}
              </button>
            </div>
          )}

          {currentRun.status === 'FAILED' && isOverLimit && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This run was refused for exceeding the recipient limit. Go to the campaign page to send anyway.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="input w-auto"
                aria-label="Filter by recipient status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as CampaignRecipientStatus | 'ALL')}
              >
                <option value="ALL">All statuses</option>
                {RECIPIENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input
                type="text"
                className="input flex-1 min-w-[160px]"
                placeholder="Search email…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-1 py-1 w-6"><span className="sr-only">Expand</span></th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">Source</th>
                    <th className="px-2 py-1 text-left">Status</th>
                    <th className="px-2 py-1 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 dark:text-gray-100">
                  {isLoading ? (
                    <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                  ) : recipients.length === 0 ? (
                    <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-400 dark:text-gray-500">No recipients match</td></tr>
                  ) : recipients.map((r) => {
                    const isExpanded = expandedIds.has(r.id);
                    return (
                      <Fragment key={r.id}>
                        <tr className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-1 py-1">
                            <button
                              type="button"
                              className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              onClick={() => toggleExpanded(r.id)}
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${r.email}`}
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">{r.email}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{r.source}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{r.status}</td>
                          <td className="px-2 py-1">{r.statusReason ?? '—'}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="space-y-3">
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Merge values</p>
                                  {sortedMergeEntries(r.mergeValues).length === 0 ? (
                                    <p className="text-xs text-gray-400 dark:text-gray-500">No merge values recorded</p>
                                  ) : (
                                    <dl className="space-y-1">
                                      {sortedMergeEntries(r.mergeValues).map(([key, value]) => (
                                        <div key={key} className="flex flex-col sm:flex-row sm:gap-2 text-xs">
                                          <dt data-testid="merge-value-key" className="font-mono text-gray-500 dark:text-gray-400 sm:w-48 sm:flex-shrink-0 break-all">
                                            {key}
                                          </dt>
                                          <dd className="text-gray-900 dark:text-gray-100 break-all">{value ?? '—'}</dd>
                                        </div>
                                      ))}
                                    </dl>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status reason</p>
                                  <p className="text-xs text-gray-900 dark:text-gray-100 break-words">{r.statusReason ?? 'None recorded'}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
              <span>{pagination ? `${pagination.totalCount} recipient${pagination.totalCount === 1 ? '' : 's'}` : ''}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary px-2 py-1"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                >
                  Prev
                </button>
                <span>Page {page}{pagination ? ` of ${Math.max(1, pagination.totalPages)}` : ''}</span>
                <button
                  type="button"
                  className="btn-secondary px-2 py-1"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination?.hasNextPage || isLoading}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
